import * as Sentry from '@sentry/react';
import { call, fork, put, select } from 'redux-saga/effects';
import { acquireLease, getOne } from 'api/cloudsave/cloudsaveApi';
import {
  AuthenticationError,
  HttpError,
  NetworkError,
  TimeoutError,
} from 'api/apiClient';
import { getCurrentUserId } from 'auth/userIdentity';
import { errorMessage } from 'containers/Snackbar/snackbarMessages';
import { getIsOnline } from 'store/connection/selectors';
import { setFileStatus } from 'store/fileState/reducer';
import { FileStatus } from 'store/fileState/types';
import type { RootState } from 'store';
import { withRetry } from 'store/sagaUtils';
import { addMessage } from 'store/snackbar/reducer';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import { generateMissingThumbnail, setRequestStatus } from './reducer';
import type { GetStorageUnitRequestActionInterface } from './sagaActions';
import type { StorageUnit } from './types';
import { LeaseType, RequestStatus, StorageUnitStatus } from './types';
import {
  evaluateLeaseState,
  hasPendingLocalChanges,
  hasPendingLocalFileDataChanges,
  mergeRemoteStorageUnitWithPendingLocal,
  toTimestamp,
} from './utils';
import {
  hydrateDesignerFromStorageUnit,
  setCurrentFileFromStorageUnit,
} from './documentSagas';

function* fetchAndUpdateStorageUnit(
  id: string,
  cachedData: StorageUnit | null,
  fileStatus: FileStatus,
  hasData: boolean,
) {
  try {
    const response: Awaited<ReturnType<typeof getOne>> = yield withRetry(() =>
      getOne(id),
    );
    const parsedBody = response.parsedBody;

    const latestCached: StorageUnit | null = yield call(
      [storageUnitCache, storageUnitCache.getOne],
      id,
    );

    const remoteUpdatedAtMs = toTimestamp(
      parsedBody?.updatedAt ?? parsedBody?.createdAt,
    );
    const cachedUpdatedAtMs = toTimestamp(
      latestCached?.localUpdatedAt ??
        latestCached?.updatedAt ??
        latestCached?.createdAt,
    );

    if (
      latestCached?.fileData &&
      remoteUpdatedAtMs !== null &&
      cachedUpdatedAtMs !== null &&
      remoteUpdatedAtMs < cachedUpdatedAtMs
    ) {
      return;
    }

    // If this file still has local pending edits, keep the cached version until
    // reconnect sync finishes. Otherwise a stale server fetch can overwrite the
    // offline-edited file before it has been uploaded.
    if (hasPendingLocalFileDataChanges(latestCached)) {
      return;
    }

    if (parsedBody?.fileData !== undefined) {
      let pendingMetadataUnit: StorageUnit | null = null;

      if (
        latestCached &&
        hasPendingLocalChanges(latestCached) &&
        latestCached.fileData === undefined
      ) {
        pendingMetadataUnit = latestCached;
      }

      const remoteToCache = pendingMetadataUnit
        ? mergeRemoteStorageUnitWithPendingLocal(
            parsedBody,
            pendingMetadataUnit,
          )
        : parsedBody;

      const cacheOptions = pendingMetadataUnit
        ? {
            markSynced: false,
            pendingAction: pendingMetadataUnit.pendingAction ?? 'update',
            syncedAt: pendingMetadataUnit.syncedAt ?? undefined,
            localUpdatedAt: pendingMetadataUnit.localUpdatedAt ?? undefined,
          }
        : undefined;
      const syncedRemote: StorageUnit = yield storageUnitCache.setOne(
        remoteToCache,
        cacheOptions,
      );

      const evaluation = evaluateLeaseState(
        syncedRemote,
        FileStatus.EDIT,
        getCurrentUserId(),
      );
      fileStatus = evaluation.status;

      const hasChanged =
        !cachedData ||
        JSON.stringify(cachedData.fileData) !==
          JSON.stringify(syncedRemote.fileData) ||
        cachedData.name !== syncedRemote.name ||
        cachedData.status !== syncedRemote.status ||
        JSON.stringify(cachedData.flowsheetMetadata) !==
          JSON.stringify(syncedRemote.flowsheetMetadata);

      if (hasChanged) {
        if (syncedRemote.status.includes(StorageUnitStatus.TEMPLATE)) {
          fileStatus = FileStatus.TEMPLATE;
        }

        yield put(setFileStatus(FileStatus.LOADING));
        yield* hydrateDesignerFromStorageUnit(syncedRemote);
      }

      yield* setCurrentFileFromStorageUnit(syncedRemote);
      yield put(setFileStatus(fileStatus));

      if (!hasData) {
        yield put(setRequestStatus(RequestStatus.SUCCESS));
      }

      if (!syncedRemote.thumbnail) {
        yield put(generateMissingThumbnail(syncedRemote.id));
      }
    }
  } catch (fetchError) {
    const isMissingOrForbidden =
      fetchError instanceof AuthenticationError ||
      (fetchError instanceof HttpError &&
        [401, 403, 404].includes(fetchError.status));

    const isExpectedBackgroundRefreshFailure =
      hasData &&
      (fetchError instanceof NetworkError ||
        fetchError instanceof TimeoutError);

    if (!isExpectedBackgroundRefreshFailure && !isMissingOrForbidden) {
      Sentry.captureException(fetchError, {
        tags: { saga: 'fetchAndUpdateStorageUnit' },
        extra: { id, fileStatus, hasData },
      });
    }

    if (!hasData) {
      yield put(
        addMessage(
          isMissingOrForbidden
            ? errorMessage(
                "This flowsheet doesn't exist or you don't have access to it.",
              )
            : errorMessage("Couldn't fetch flowsheet. Please try again later."),
        ),
      );
      yield put(setRequestStatus(RequestStatus.FAILURE));
    }
  }
}

export function* getStorageUnit(action: GetStorageUnitRequestActionInterface) {
  const requestStatus: RequestStatus = yield select(
    (s: RootState) => s.storageUnitsState.requestStatus,
  );

  if (requestStatus === RequestStatus.REQUESTED) {
    return;
  }

  yield put(setFileStatus(FileStatus.LOADING));
  yield put(setRequestStatus(RequestStatus.REQUESTED));

  const { id } = action.payload;
  let fileStatus = FileStatus.EDIT;
  let cachedData = null;
  let hasData = false;
  const currentUserId = getCurrentUserId();

  const isOnline: boolean = yield select(getIsOnline);

  if (isOnline) {
    try {
      yield acquireLease(id, LeaseType.Soft);
    } catch (err) {
      // NOTE: Expected when another user holds an exclusive lease. We'll evaluate access after fetching the storage unit.
    }
  }

  try {
    cachedData = (yield storageUnitCache.getOne(id)) as Awaited<
      ReturnType<typeof storageUnitCache.getOne>
    >;
  } catch (cacheError) {
    Sentry.captureException(cacheError, {
      tags: { saga: 'getStorageUnit', subsystem: 'cache' },
      level: 'warning',
    });
  }

  if (cachedData?.fileData) {
    const evaluation = evaluateLeaseState(
      cachedData,
      fileStatus,
      currentUserId,
    );
    fileStatus = evaluation.status;

    yield* hydrateDesignerFromStorageUnit(cachedData);
    yield* setCurrentFileFromStorageUnit(cachedData, false);
    yield put(setRequestStatus(RequestStatus.SUCCESS));
    yield put(setFileStatus(fileStatus));
    hasData = true;
  }

  if (!isOnline) {
    if (!hasData) {
      yield put(
        addMessage(
          errorMessage(
            "Couldn't open flowsheet offline. This browser doesn't have a cached copy.",
          ),
        ),
      );
      yield put(setRequestStatus(RequestStatus.FAILURE));
    }
    return;
  }

  yield fork(fetchAndUpdateStorageUnit, id, cachedData, fileStatus, hasData);
}
